import { recordOriginalGeometryPositions } from '../modelGeometry/meshOriginalGeometry';
import {
  getBoxInObjectSpace,
  recomputeGeometryBounds,
  setVertexFromObjectLocal,
} from '../modelGeometry/objectSpaceGeometry';
import { TALL_CABINET_TOE_KICK_HEIGHT } from '../../../../../../../helpers/tallCabinetInteriorHelper';
import {
  isCabinetSectionHighlightBox,
  withSectionHighlightBoxesHidden,
} from '../cabinetInterior/tallCabinetSectionHighlightBoxes';

const SECTION_MESH_NAME = /^(Door[ _](Left|Right)|Drawer|Shelf)(?:\d*|\.\d+)?($|_)/;
const ORIGINAL_GEOMETRY_TAG = 'tallCabinetHeightOriginalGeometry';
const ORIGINAL_BOUNDS_TAG = 'tallCabinetHeightOriginalBounds';
const HEIGHT_DELTA_TAG = 'tallCabinetHeightDeltaLocal';
const TOP_BAND_RATIO = 0.08;

function isSectionMesh(object) {
  let current = object;

  while (current && current.isObject3D) {
    if (SECTION_MESH_NAME.test(current.name || '')) {
      return true;
    }

    current = current.parent;
  }

  return false;
}

function recordInstanceBounds(instance) {
  if (instance.userData[ORIGINAL_BOUNDS_TAG]) {
    return instance.userData[ORIGINAL_BOUNDS_TAG];
  }

  const box = withSectionHighlightBoxesHidden(instance, () => {
    instance.updateMatrixWorld(true);
    return getBoxInObjectSpace(instance, instance);
  });
  const bounds = {
    minY: box.min.y,
    maxY: box.max.y,
    height: box.max.y - box.min.y,
  };

  instance.userData[ORIGINAL_BOUNDS_TAG] = bounds;
  return bounds;
}

function getResizedBodyVertexY(vertexY, resizeBounds) {
  const {
    sourceBottomFixedTopY,
    sourceTopBandStartY,
    sourceTopY,
    targetTopBandStartY,
    targetTopY,
  } = resizeBounds;

  if (vertexY <= sourceBottomFixedTopY) {
    return vertexY;
  }

  if (vertexY >= sourceTopBandStartY) {
    return targetTopY - (sourceTopY - vertexY);
  }

  const sourceMiddleHeight = sourceTopBandStartY - sourceBottomFixedTopY;
  const targetMiddleHeight = targetTopBandStartY - sourceBottomFixedTopY;

  if (sourceMiddleHeight <= 0 || targetMiddleHeight <= 0) {
    return vertexY;
  }

  const middleRatio = (vertexY - sourceBottomFixedTopY) / sourceMiddleHeight;
  return sourceBottomFixedTopY + middleRatio * targetMiddleHeight;
}

function resizeBodyMesh(mesh, instance, originalBounds, cabinetInitialHeight, targetTopY) {
  if (isSectionMesh(mesh) || isCabinetSectionHighlightBox(mesh)) {
    return;
  }

  const originalGeometry = recordOriginalGeometryPositions(mesh, ORIGINAL_GEOMETRY_TAG, null, null);
  if (!originalGeometry) {
    return;
  }

  mesh.updateMatrixWorld(true);

  const THREE = window.THREE;
  const vertex = new THREE.Vector3();
  const instanceLocalVertex = new THREE.Vector3();
  const sourceBottomY = originalBounds.minY;
  const sourceTopY = originalBounds.maxY;
  const topBandHeight = originalBounds.height * TOP_BAND_RATIO;
  const sourceTopBandStartY = sourceTopY - topBandHeight;
  const targetTopBandStartY = targetTopY - topBandHeight;
  const sourceBottomFixedTopY = sourceBottomY
    + originalBounds.height * (TALL_CABINET_TOE_KICK_HEIGHT / cabinetInitialHeight);
  const position = mesh.geometry.attributes.position;
  const resizeBounds = {
    sourceBottomFixedTopY,
    sourceTopBandStartY,
    sourceTopY,
    targetTopBandStartY,
    targetTopY,
  };

  for (let index = 0; index < originalGeometry.positions.length; index += 3) {
    vertex.set(
      originalGeometry.positions[index],
      originalGeometry.positions[index + 1],
      originalGeometry.positions[index + 2]
    );

    mesh.localToWorld(vertex);
    instance.worldToLocal(vertex);

    instanceLocalVertex.copy(vertex);
    instanceLocalVertex.y = getResizedBodyVertexY(vertex.y, resizeBounds);

    setVertexFromObjectLocal(mesh, instance, vertex, instanceLocalVertex);

    position.array[index] = vertex.x;
    position.array[index + 1] = vertex.y;
    position.array[index + 2] = vertex.z;
  }

  position.needsUpdate = true;
  recomputeGeometryBounds(mesh.geometry);
}

export function applyTallCabinetHeight(instance, shape) {
  if (!instance || !shape) {
    return;
  }

  const cabinetInitialHeight = shape.getInitialHeight();
  const cabinetHeight = shape.getHeight();
  const normalizedCabinetInitialHeight = Number(cabinetInitialHeight);
  const normalizedCabinetHeight = Number(cabinetHeight);

  if (
    !normalizedCabinetInitialHeight
    || !normalizedCabinetHeight
    || normalizedCabinetInitialHeight <= 0
    || normalizedCabinetHeight <= 0
  ) {
    return;
  }

  const originalBounds = recordInstanceBounds(instance);
  if (!originalBounds.height || originalBounds.height <= 0) {
    return;
  }

  const targetLocalHeight = originalBounds.height * (normalizedCabinetHeight / normalizedCabinetInitialHeight);
  const targetTopY = originalBounds.minY + targetLocalHeight;
  instance.userData[HEIGHT_DELTA_TAG] = targetTopY - originalBounds.maxY;

  instance.traverse((child) => {
    if (child.isMesh && !isCabinetSectionHighlightBox(child)) {
      resizeBodyMesh(child, instance, originalBounds, normalizedCabinetInitialHeight, targetTopY);
    }
  });
}

export function getTallCabinetHeightDelta(instance) {
  return instance && Number(instance.userData[HEIGHT_DELTA_TAG]) || 0;
}
